import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Users, Plus, Edit, Trash2, UserPlus, UserMinus, Search, 
  FileText, Download, FileSpreadsheet, Loader2, Target, Calendar
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CAMPAIGN_PURPOSES = [
  { id: 'newsletter', label: 'Newsletter' },
  { id: 'promotions', label: 'Promoções' },
  { id: 'events', label: 'Eventos' },
  { id: 'surveys', label: 'Inquéritos' },
  { id: 'product_updates', label: 'Atualizações de Produto' }
];

const LEGAL_BASIS_OPTIONS = [
  { id: 'art6_1a', label: 'Art. 6º(1)(a) - Consentimento' },
  { id: 'art6_1b', label: 'Art. 6º(1)(b) - Execução de contrato' },
  { id: 'art6_1c', label: 'Art. 6º(1)(c) - Obrigação legal' },
  { id: 'art6_1f', label: 'Art. 6º(1)(f) - Interesse legítimo' }
];

const COLOR_OPTIONS = [
  { value: '#3B82F6', label: 'Azul' },
  { value: '#10B981', label: 'Verde' },
  { value: '#F59E0B', label: 'Amarelo' },
  { value: '#EF4444', label: 'Vermelho' },
  { value: '#8B5CF6', label: 'Roxo' },
  { value: '#EC4899', label: 'Rosa' },
  { value: '#6366F1', label: 'Índigo' },
  { value: '#14B8A6', label: 'Turquesa' }
];

const GroupsPage = () => {
  const { user } = useOutletContext();
  const [groups, setGroups] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [titulares, setTitulares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('groups');
  
  // Group dialogs
  const [createGroupDialog, setCreateGroupDialog] = useState(false);
  const [editGroupDialog, setEditGroupDialog] = useState({ open: false, group: null });
  const [addMembersDialog, setAddMembersDialog] = useState({ open: false, group: null });
  
  // Campaign dialogs
  const [createCampaignDialog, setCreateCampaignDialog] = useState(false);
  const [campaignDetailsDialog, setCampaignDetailsDialog] = useState({ open: false, campaign: null });
  
  // Form states
  const [groupForm, setGroupForm] = useState({ name: '', description: '', color: '#3B82F6' });
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    description: '',
    purpose: '',
    legal_basis: 'Art. 6º(1)(a) - Consentimento',
    data_categories: ['email', 'nome'],
    target_group_ids: [],
    channels: ['email'],
    retention_period_days: 365
  });
  const [selectedTitulares, setSelectedTitulares] = useState([]);
  
  // ROPA export loading
  const [exportingPDF, setExportingPDF] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [groupsRes, campaignsRes, titularesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/titular-groups`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/marketing-campaigns`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/titulares`, { withCredentials: true })
      ]);
      setGroups(groupsRes.data);
      setCampaigns(campaignsRes.data);
      setTitulares(titularesRes.data?.titulares || titularesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // Group functions
  const handleCreateGroup = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/titular-groups`, groupForm, { withCredentials: true });
      toast.success('Grupo criado com sucesso');
      setCreateGroupDialog(false);
      setGroupForm({ name: '', description: '', color: '#3B82F6' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar grupo');
    }
  };

  const handleUpdateGroup = async () => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/titular-groups/${editGroupDialog.group.group_id}`,
        groupForm,
        { withCredentials: true }
      );
      toast.success('Grupo atualizado');
      setEditGroupDialog({ open: false, group: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar grupo');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Tem certeza que deseja eliminar este grupo?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/titular-groups/${groupId}`, { withCredentials: true });
      toast.success('Grupo eliminado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao eliminar grupo');
    }
  };

  const handleAddMembers = async () => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/titular-groups/${addMembersDialog.group.group_id}/add-titulares`,
        selectedTitulares,
        { withCredentials: true }
      );
      toast.success('Titulares adicionados ao grupo');
      setAddMembersDialog({ open: false, group: null });
      setSelectedTitulares([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar titulares');
    }
  };

  // Campaign functions
  const handleCreateCampaign = async () => {
    if (!campaignForm.name || !campaignForm.purpose || campaignForm.target_group_ids.length === 0) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    try {
      await axios.post(`${BACKEND_URL}/api/marketing-campaigns`, campaignForm, { withCredentials: true });
      toast.success('Campanha criada com sucesso');
      setCreateCampaignDialog(false);
      setCampaignForm({
        name: '',
        description: '',
        purpose: '',
        legal_basis: 'Art. 6º(1)(a) - Consentimento',
        data_categories: ['email', 'nome'],
        target_group_ids: [],
        channels: ['email'],
        retention_period_days: 365
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar campanha');
    }
  };

  const handleApproveCampaign = async (campaignId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/marketing-campaigns/${campaignId}/approve`, {}, { withCredentials: true });
      toast.success('Campanha aprovada');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao aprovar campanha');
    }
  };

  const handleExecuteCampaign = async (campaignId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/marketing-campaigns/${campaignId}/execute`, {}, { withCredentials: true });
      toast.success('Campanha marcada como executada');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao executar campanha');
    }
  };

  // ROPA export functions
  const handleExportROPAPDF = async () => {
    setExportingPDF(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/ropa/export/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ROPA_Privaxia_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Relatório ROPA exportado em PDF');
    } catch (error) {
      toast.error('Erro ao exportar ROPA em PDF');
    } finally {
      setExportingPDF(false);
    }
  };

  const handleExportROPAExcel = async () => {
    setExportingExcel(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/ropa/export/excel`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ROPA_Privaxia_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Relatório ROPA exportado em Excel');
    } catch (error) {
      toast.error('Erro ao exportar ROPA em Excel');
    } finally {
      setExportingExcel(false);
    }
  };

  const getStatusBadge = (status, dpoApproved) => {
    const statusConfig = {
      draft: { label: 'Rascunho', variant: 'secondary' },
      pending_approval: { label: 'Aguarda Aprovação', variant: 'warning' },
      approved: { label: 'Aprovado', variant: 'success' },
      executed: { label: 'Executado', variant: 'default' },
      cancelled: { label: 'Cancelado', variant: 'destructive' }
    };
    const config = statusConfig[status] || { label: status, variant: 'secondary' };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const filteredGroups = groups.filter(g => 
    g.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (g.description && g.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const filteredCampaigns = campaigns.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="groups-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Grupos & Campanhas</h1>
          <p className="text-slate-500 mt-1">Gestão de grupos de titulares e campanhas de marketing</p>
        </div>
        <div className="flex gap-2">
          {(user?.role === 'admin' || user?.role === 'dpo') && (
            <>
              <Button
                data-testid="export-ropa-pdf-btn"
                variant="outline"
                onClick={handleExportROPAPDF}
                disabled={exportingPDF}
              >
                {exportingPDF ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                ROPA PDF
              </Button>
              <Button
                data-testid="export-ropa-excel-btn"
                variant="outline"
                onClick={handleExportROPAExcel}
                disabled={exportingExcel}
              >
                {exportingExcel ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
                ROPA Excel
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{groups.length}</p>
                <p className="text-sm text-slate-500">Grupos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Target className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{campaigns.length}</p>
                <p className="text-sm text-slate-500">Campanhas</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Calendar className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{campaigns.filter(c => !c.dpo_approved).length}</p>
                <p className="text-sm text-slate-500">Aguardam Aprovação</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <FileText className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{groups.reduce((acc, g) => acc + (g.titular_count || 0), 0)}</p>
                <p className="text-sm text-slate-500">Titulares em Grupos</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="groups" data-testid="tab-groups">Grupos de Titulares</TabsTrigger>
            <TabsTrigger value="campaigns" data-testid="tab-campaigns">Campanhas de Marketing</TabsTrigger>
          </TabsList>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="search-input"
                placeholder="Pesquisar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64"
              />
            </div>
            {activeTab === 'groups' && (
              <Button data-testid="create-group-btn" onClick={() => setCreateGroupDialog(true)}>
                <Plus className="w-4 h-4 mr-2" /> Novo Grupo
              </Button>
            )}
            {activeTab === 'campaigns' && (
              <Button data-testid="create-campaign-btn" onClick={() => setCreateCampaignDialog(true)}>
                <Plus className="w-4 h-4 mr-2" /> Nova Campanha
              </Button>
            )}
          </div>
        </div>

        {/* Groups Tab */}
        <TabsContent value="groups">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cor</TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead className="text-center">Titulares</TableHead>
                    <TableHead>Criado em</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredGroups.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        Nenhum grupo encontrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredGroups.map(group => (
                      <TableRow key={group.group_id} data-testid={`group-row-${group.group_id}`}>
                        <TableCell>
                          <div 
                            className="w-6 h-6 rounded-full" 
                            style={{ backgroundColor: group.color || '#3B82F6' }}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{group.name}</TableCell>
                        <TableCell className="text-slate-500 max-w-xs truncate">
                          {group.description || '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="secondary">{group.titular_count || 0}</Badge>
                        </TableCell>
                        <TableCell className="text-slate-500">
                          {new Date(group.created_at).toLocaleDateString('pt-PT')}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              data-testid={`add-members-${group.group_id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setAddMembersDialog({ open: true, group });
                                setSelectedTitulares([]);
                              }}
                            >
                              <UserPlus className="w-4 h-4" />
                            </Button>
                            <Button
                              data-testid={`edit-group-${group.group_id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setGroupForm({
                                  name: group.name,
                                  description: group.description || '',
                                  color: group.color || '#3B82F6'
                                });
                                setEditGroupDialog({ open: true, group });
                              }}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            {(user?.role === 'admin' || user?.role === 'dpo') && (
                              <Button
                                data-testid={`delete-group-${group.group_id}`}
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDeleteGroup(group.group_id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Finalidade</TableHead>
                    <TableHead>Grupos</TableHead>
                    <TableHead className="text-center">Membros</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Criado em</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCampaigns.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                        Nenhuma campanha encontrada
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCampaigns.map(campaign => (
                      <TableRow key={campaign.campaign_id} data-testid={`campaign-row-${campaign.campaign_id}`}>
                        <TableCell className="font-medium">{campaign.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {CAMPAIGN_PURPOSES.find(p => p.id === campaign.purpose)?.label || campaign.purpose}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {campaign.target_groups_info?.map(g => (
                              <Badge key={g.group_id} variant="secondary" className="text-xs">
                                {g.name}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge>{campaign.members_count || 0}</Badge>
                        </TableCell>
                        <TableCell>{getStatusBadge(campaign.status, campaign.dpo_approved)}</TableCell>
                        <TableCell className="text-slate-500">
                          {new Date(campaign.created_at).toLocaleDateString('pt-PT')}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              data-testid={`view-campaign-${campaign.campaign_id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => setCampaignDetailsDialog({ open: true, campaign })}
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                            {(user?.role === 'admin' || user?.role === 'dpo') && !campaign.dpo_approved && (
                              <Button
                                data-testid={`approve-campaign-${campaign.campaign_id}`}
                                variant="ghost"
                                size="sm"
                                className="text-green-600 hover:text-green-700"
                                onClick={() => handleApproveCampaign(campaign.campaign_id)}
                              >
                                Aprovar
                              </Button>
                            )}
                            {campaign.dpo_approved && campaign.status !== 'executed' && (
                              <Button
                                data-testid={`execute-campaign-${campaign.campaign_id}`}
                                variant="ghost"
                                size="sm"
                                className="text-blue-600 hover:text-blue-700"
                                onClick={() => handleExecuteCampaign(campaign.campaign_id)}
                              >
                                Executar
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Group Dialog */}
      <Dialog open={createGroupDialog} onOpenChange={setCreateGroupDialog}>
        <DialogContent data-testid="create-group-dialog">
          <DialogHeader>
            <DialogTitle>Criar Novo Grupo</DialogTitle>
            <DialogDescription>Crie um grupo para organizar titulares de dados</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="group-name">Nome do Grupo *</Label>
              <Input
                id="group-name"
                data-testid="group-name-input"
                value={groupForm.name}
                onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                placeholder="Ex: Clientes Premium"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="group-description">Descrição</Label>
              <Input
                id="group-description"
                data-testid="group-description-input"
                value={groupForm.description}
                onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                placeholder="Descrição do grupo..."
              />
            </div>
            <div className="space-y-2">
              <Label>Cor</Label>
              <div className="flex gap-2">
                {COLOR_OPTIONS.map(color => (
                  <button
                    key={color.value}
                    className={`w-8 h-8 rounded-full border-2 ${groupForm.color === color.value ? 'border-slate-900' : 'border-transparent'}`}
                    style={{ backgroundColor: color.value }}
                    onClick={() => setGroupForm({ ...groupForm, color: color.value })}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateGroupDialog(false)}>Cancelar</Button>
            <Button data-testid="submit-create-group" onClick={handleCreateGroup} disabled={!groupForm.name}>
              Criar Grupo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Group Dialog */}
      <Dialog open={editGroupDialog.open} onOpenChange={(open) => setEditGroupDialog({ open, group: null })}>
        <DialogContent data-testid="edit-group-dialog">
          <DialogHeader>
            <DialogTitle>Editar Grupo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome do Grupo *</Label>
              <Input
                value={groupForm.name}
                onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Descrição</Label>
              <Input
                value={groupForm.description}
                onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Cor</Label>
              <div className="flex gap-2">
                {COLOR_OPTIONS.map(color => (
                  <button
                    key={color.value}
                    className={`w-8 h-8 rounded-full border-2 ${groupForm.color === color.value ? 'border-slate-900' : 'border-transparent'}`}
                    style={{ backgroundColor: color.value }}
                    onClick={() => setGroupForm({ ...groupForm, color: color.value })}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditGroupDialog({ open: false, group: null })}>Cancelar</Button>
            <Button onClick={handleUpdateGroup}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Members Dialog */}
      <Dialog open={addMembersDialog.open} onOpenChange={(open) => setAddMembersDialog({ open, group: null })}>
        <DialogContent className="max-w-2xl" data-testid="add-members-dialog">
          <DialogHeader>
            <DialogTitle>Adicionar Titulares ao Grupo</DialogTitle>
            <DialogDescription>
              Grupo: {addMembersDialog.group?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="max-h-96 overflow-y-auto border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12"></TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>Email</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {titulares.map(titular => (
                    <TableRow key={titular.user_id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedTitulares.includes(titular.user_id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedTitulares([...selectedTitulares, titular.user_id]);
                            } else {
                              setSelectedTitulares(selectedTitulares.filter(id => id !== titular.user_id));
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>{titular.name}</TableCell>
                      <TableCell className="text-slate-500">{titular.email}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <p className="text-sm text-slate-500 mt-2">
              {selectedTitulares.length} titular(es) selecionado(s)
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddMembersDialog({ open: false, group: null })}>Cancelar</Button>
            <Button data-testid="confirm-add-members" onClick={handleAddMembers} disabled={selectedTitulares.length === 0}>
              Adicionar {selectedTitulares.length} Titular(es)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Campaign Dialog */}
      <Dialog open={createCampaignDialog} onOpenChange={setCreateCampaignDialog}>
        <DialogContent className="max-w-2xl" data-testid="create-campaign-dialog">
          <DialogHeader>
            <DialogTitle>Criar Nova Campanha</DialogTitle>
            <DialogDescription>Crie uma campanha de marketing direcionada a grupos de titulares</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome da Campanha *</Label>
                <Input
                  data-testid="campaign-name-input"
                  value={campaignForm.name}
                  onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
                  placeholder="Ex: Campanha de Natal 2026"
                />
              </div>
              <div className="space-y-2">
                <Label>Finalidade *</Label>
                <Select
                  value={campaignForm.purpose}
                  onValueChange={(value) => setCampaignForm({ ...campaignForm, purpose: value })}
                >
                  <SelectTrigger data-testid="campaign-purpose-select">
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {CAMPAIGN_PURPOSES.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Descrição</Label>
              <Input
                data-testid="campaign-description-input"
                value={campaignForm.description}
                onChange={(e) => setCampaignForm({ ...campaignForm, description: e.target.value })}
                placeholder="Descrição da campanha..."
              />
            </div>

            <div className="space-y-2">
              <Label>Grupos Alvo *</Label>
              <div className="border rounded-lg p-3 space-y-2 max-h-32 overflow-y-auto">
                {groups.map(group => (
                  <div key={group.group_id} className="flex items-center gap-2">
                    <Checkbox
                      id={`group-${group.group_id}`}
                      checked={campaignForm.target_group_ids.includes(group.group_id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setCampaignForm({
                            ...campaignForm,
                            target_group_ids: [...campaignForm.target_group_ids, group.group_id]
                          });
                        } else {
                          setCampaignForm({
                            ...campaignForm,
                            target_group_ids: campaignForm.target_group_ids.filter(id => id !== group.group_id)
                          });
                        }
                      }}
                    />
                    <Label htmlFor={`group-${group.group_id}`} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: group.color }} />
                      {group.name}
                      <Badge variant="secondary" className="text-xs">{group.titular_count || 0}</Badge>
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Base Legal</Label>
                <Select
                  value={campaignForm.legal_basis}
                  onValueChange={(value) => setCampaignForm({ ...campaignForm, legal_basis: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LEGAL_BASIS_OPTIONS.map(l => (
                      <SelectItem key={l.id} value={l.label}>{l.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Prazo de Conservação (dias)</Label>
                <Input
                  type="number"
                  value={campaignForm.retention_period_days}
                  onChange={(e) => setCampaignForm({ ...campaignForm, retention_period_days: parseInt(e.target.value) })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Canais</Label>
              <div className="flex gap-4">
                {['email', 'sms', 'postal'].map(channel => (
                  <div key={channel} className="flex items-center gap-2">
                    <Checkbox
                      id={`channel-${channel}`}
                      checked={campaignForm.channels.includes(channel)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setCampaignForm({
                            ...campaignForm,
                            channels: [...campaignForm.channels, channel]
                          });
                        } else {
                          setCampaignForm({
                            ...campaignForm,
                            channels: campaignForm.channels.filter(c => c !== channel)
                          });
                        }
                      }}
                    />
                    <Label htmlFor={`channel-${channel}`} className="capitalize">{channel}</Label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateCampaignDialog(false)}>Cancelar</Button>
            <Button 
              data-testid="submit-create-campaign" 
              onClick={handleCreateCampaign}
              disabled={!campaignForm.name || !campaignForm.purpose || campaignForm.target_group_ids.length === 0}
            >
              Criar Campanha
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Campaign Details Dialog */}
      <Dialog open={campaignDetailsDialog.open} onOpenChange={(open) => setCampaignDetailsDialog({ open, campaign: null })}>
        <DialogContent className="max-w-2xl" data-testid="campaign-details-dialog">
          <DialogHeader>
            <DialogTitle>{campaignDetailsDialog.campaign?.name}</DialogTitle>
            <DialogDescription>Detalhes da campanha de marketing</DialogDescription>
          </DialogHeader>
          {campaignDetailsDialog.campaign && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">Finalidade</Label>
                  <p className="font-medium">
                    {CAMPAIGN_PURPOSES.find(p => p.id === campaignDetailsDialog.campaign.purpose)?.label || campaignDetailsDialog.campaign.purpose}
                  </p>
                </div>
                <div>
                  <Label className="text-slate-500">Estado</Label>
                  <div className="mt-1">{getStatusBadge(campaignDetailsDialog.campaign.status, campaignDetailsDialog.campaign.dpo_approved)}</div>
                </div>
                <div>
                  <Label className="text-slate-500">Base Legal</Label>
                  <p className="font-medium">{campaignDetailsDialog.campaign.legal_basis}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Prazo de Conservação</Label>
                  <p className="font-medium">{campaignDetailsDialog.campaign.retention_period_days} dias</p>
                </div>
              </div>
              
              {campaignDetailsDialog.campaign.description && (
                <div>
                  <Label className="text-slate-500">Descrição</Label>
                  <p>{campaignDetailsDialog.campaign.description}</p>
                </div>
              )}

              <div>
                <Label className="text-slate-500">Grupos Alvo</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {campaignDetailsDialog.campaign.target_groups_info?.map(g => (
                    <Badge key={g.group_id} variant="secondary">{g.name}</Badge>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-slate-500">Canais</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {campaignDetailsDialog.campaign.channels?.map(c => (
                    <Badge key={c} variant="outline" className="capitalize">{c}</Badge>
                  ))}
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{campaignDetailsDialog.campaign.members_count || 0}</p>
                    <p className="text-sm text-slate-500">Membros no Snapshot</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">
                      {campaignDetailsDialog.campaign.dpo_approved ? 'Sim' : 'Não'}
                    </p>
                    <p className="text-sm text-slate-500">Aprovado DPO</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">
                      {campaignDetailsDialog.campaign.created_by_name || '-'}
                    </p>
                    <p className="text-sm text-slate-500">Criado por</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCampaignDetailsDialog({ open: false, campaign: null })}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GroupsPage;
